import { EditOutlined } from "@ant-design/icons";
import { Button, Collapse, CollapseProps } from "antd";
import TextArea from "antd/es/input/TextArea";
import { editCandidate } from "api/candidate.api";
import { Title5 } from "components/Common/Typography/Titles";
import React, { useEffect, useState } from "react";
import editIcon from "../../images/pencil-02.svg";

const Text = (key: any, desc: any) => (
  <>
    <Title5 className="font-semibold capitalize" text={key} />
    <p className="text-text-secondary text-sm font-normal">{desc}</p>
  </>
);

interface RelatedQuestionsProps {
  isBioEdited?: boolean;
  editorName: string;
  candidateId: number;
  error: boolean;
  AIBioData: any; // Optional string
  loading: boolean;
  bio: any;
  bgColor: string;
  setRefresh?: React.Dispatch<React.SetStateAction<boolean>>;
  isListed?: boolean;
}

const RelatedQuestions: React.FC<RelatedQuestionsProps> = ({ isBioEdited, error, AIBioData, loading, editorName, candidateId, bio, setRefresh, isListed, bgColor }) => {
  const safeParse = (value: any) => {
    if (value) {
      try {
        return typeof value === "string" ? JSON.parse(value) : value;
      } catch {
        return null;
      }
    } else {
      return null;
    }
  };

  const [activePanel, setActivePanel] = useState<string | string[]>();
  const [bioError, setBioError] = useState(false);
  const [shouldUpdateBio, setShouldUpdateBio] = useState(false);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editValue, setEditValue] = useState<string>("");
  const [data, setData] = useState(() => {
    const parsed = isBioEdited ? safeParse(bio)?.advancedSummary : AIBioData || safeParse(bio);
    return parsed || "";
  });

  useEffect(() => {
    if (AIBioData && (!data || !isBioEdited)) {
      console.log("AIBioData: ", AIBioData);
      console.log("BIO64: ", isBioEdited, data);
      setData(AIBioData);
    }
  }, [AIBioData]);

  const sortedKeys = Object.keys(
    (isBioEdited ? data : AIBioData) || {}
  )
    .filter((key) => {
      const source = isBioEdited ? data : AIBioData;
      return (
        key !== "overview" &&
        key !== "bio" &&
        (source ? source[key] : false)
      );
    })
    .sort((a, b) => {
      if (a === "personalLife") return -1;
      if (b === "personalLife") return 1;
      if (a === "citations") return 1;
      if (b === "citations") return -1;
      return 0;
    });

  const handleUpdateBio = async () => {
    try {
      const candidateBioPayload = { custom_fields: { bio: JSON.stringify({ bio: data['bio'], advancedSummary: data }), edited_by: editorName, modified_date: new Date(), is_edited: true }, candidate: candidateId };
      const response = await editCandidate(candidateBioPayload);
      if (setRefresh) setRefresh(true);
      // setIsEditingDescription(false);
    } catch (err) {
      // setError("Failed to update candidate description");
      console.log("Error while updating Bio: ", err);
    } finally {
      // setRefresh(false);
      // dispatch(creditsRefreshChanger());
    }
  };

  const handleEditClick = (key: string) => {
    setEditingKey(key);
    if (key === 'bio') {
      setEditValue(isBioEdited ? bio.bio : bio);
    } else {
      setEditValue(data[key]); // Store original value in editable state
    }
    setActivePanel(key);
  };

  const handleSave = () => {
    if (editingKey) {
      setData((prev: any) => ({ ...prev, [editingKey]: editValue }));
      setEditingKey(null);
      setShouldUpdateBio(true);
    }
  };

  const handleCancel = () => {
    setEditingKey(null);
    setEditValue("");
  };

  useEffect(() => {
    console.log("Data 185: ", data);
    console.log("Bio 186: ", bio);
    if (shouldUpdateBio) {
      handleUpdateBio();
      setShouldUpdateBio(false);
    }
  }, [data]);

  const items: CollapseProps["items"] = sortedKeys.map((key) => {
    const value = data?.[key];

    let content: React.ReactNode;
    if (value == null) {
      content = <p className="text-text-secondary text-sm font-normal">No data available.</p>;
    } else if (editingKey === key) {
      content = (
        <div className="flex flex-col gap-2">
          <TextArea
            rows={4}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
          />
          <div className="flex gap-2">
            <Button size="small" className="text-white bg-[#845dda] border border-[#7F56D9]" onClick={handleSave}>
              Save
            </Button>
            <Button size="small" onClick={handleCancel}>
              Cancel
            </Button>
          </div>
        </div>
      );
    } else if (typeof value === "string") {
      content = <p className="text-text-secondary text-sm font-normal">{value}</p>;
    } else if (Array.isArray(value)) {
      content = (
        <ul className="list-disc list-inside text-text-secondary text-sm font-normal">
          {value.map((item, index) => (
            <li key={index}>{typeof item === "string" ? item : JSON.stringify(item)}</li>
          ))}
        </ul>
      );
    } else if (typeof value === "object") {
      content = (
        <ul className="list-disc list-inside text-text-secondary text-sm font-normal">
          {Object.entries(value).map(([subKey, subValue], index) => (
            <li key={index}>
              <strong>{subKey}:</strong> {JSON.stringify(subValue)}
            </li>
          ))}
        </ul>
      );
    } else {
      content = <p className="text-text-secondary text-sm font-normal">{JSON.stringify(value)}</p>;
    }

    return {
      key,
      label: (
        <div className="flex items-center justify-between">
          <Title5 className="font-semibold capitalize" text={key.replace(/([A-Z])/g, " $1").trim()} />
          {
            editorName !== "" && (
              <img
                src={editIcon}
                alt=""
                onClick={(e) => {
                  e.stopPropagation();
                  handleEditClick(key);
                }}
              />
            )
          }
        </div>
      ),
      children: content,
    };
  });

  return (
    <>
      {data ? (
        <div className="w-full flex flex-col gap-2">
          <div className="flex items-center justify-between">
            {editingKey !== "bio" && (
              <>
                <p className="text-text-secondary text-sm font-normal">
                  {data?.bio ?? safeParse(bio) ? safeParse(bio).bio : bio ?? ""}
                </p>

                {
                  editorName !== "" && (
                    <img
                      src={editIcon}
                      alt=""
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingKey("bio");
                        setEditValue(isBioEdited ? data.bio || bio.bio : bio || "");
                      }}
                    />
                  )
                }
              </>
            )}
          </div>
          {editingKey === "bio" && (
            <>
              <TextArea
                rows={4}
                value={editValue}
                onChange={(e) => {
                  setEditValue(e.target.value);
                  if (e.target.value.trim()) {
                    setBioError(false); // Clear error on valid input
                  }
                }}
              />
              {bioError && <p className="text-red-500 text-sm mt-1">Bio is required</p>}

              <div className="flex gap-2 mt-2">
                <Button
                  size="small"
                  className="text-white bg-[#845dda] border border-[#7F56D9]"
                  onClick={() => {
                    if (!editValue.trim()) {
                      setBioError(true);
                      return;
                    }
                    setData((prev: any) => ({ ...prev, bio: editValue }));
                    setEditingKey(null);
                    handleSave();
                  }}
                >
                  Save
                </Button>
                <Button size="small" onClick={() => setEditingKey(null)}>
                  Cancel
                </Button>
              </div>
            </>
          )}
          {
            !isListed &&
            (
              <>
                {
                  (AIBioData || isBioEdited) &&
                  <div className="flex items-center justify-between">
                    <Title5 className="font-semibold capitalize" text="Overview" />
                    {editingKey !== "overview" && (
                      <>
                        {
                          editorName !== "" && (
                            <img
                              src={editIcon}
                              alt=""
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingKey("overview");
                                setEditValue(data.overview || "");
                              }}
                            />
                          )
                        }
                      </>
                    )}
                  </div>
                }

                {editingKey === "overview" ? (
                  <>
                    {/* <div className="w-full flex flex-col gap-2"> */}
                    <TextArea
                      rows={4}
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      className="w-full"
                    />
                    <div className="flex gap-2 mt-2">
                      <Button size="small" className="text-white bg-[#845dda] border border-[#7F56D9]" onClick={() => {
                        setData((prev: any) => ({ ...prev, overview: editValue }));
                        setEditingKey(null);
                        handleSave();
                      }}>
                        Save
                      </Button>
                      <Button size="small" onClick={() => setEditingKey(null)}>
                        Cancel
                      </Button>
                    </div>
                  </>
                ) : (
                  <p className="text-text-secondary text-sm font-normal w-full mb-4">{data.overview}</p>
                )}
              </>
            )
          }
        </div>
      ) : (
        !loading && <p className="text-text-secondary" style={{ marginLeft: "-1.8%" }}>No summary available</p>
        // !loading && <p className="text-text-secondary">Not found!</p>
      )}

      {
        !isListed && (
          <>
            {(AIBioData || isBioEdited) &&
              <Collapse
                activeKey={activePanel}
                onChange={(key) => setActivePanel(key)} // allow user toggle manually too
                className={`no-outer-border ${bgColor ? bgColor : "bg-white"}, "px-4"`}
                items={items}
                bordered={true}
                expandIcon={({ isActive }) => (
                  <span style={{ fontWeight: 'bold', fontSize: '18px', marginRight: 8 }} >
                    {isActive ? '−' : '+'}
                  </span>
                )}
              />
            }
          </>
        )}
      {/* {(AIBioData || isBioEdited) && <Collapse className="bg-white" items={items} bordered={false} />} */}
    </>
  );
};

export default RelatedQuestions;